// frontend/src/components/analytics/MaintenanceAnalytics.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Wrench, TrendingUp, AlertCircle, Clock, CheckCircle, XCircle,
  Calendar, Download, RefreshCw, AlertTriangle, Target
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const MaintenanceAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = { period };
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const response = await axios.get(`${API_BASE}/analytics/maintenance/dashboard`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error loading maintenance analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
        const token = localStorage.getItem('access_token');
        const start = startDate || data?.period?.start;
        const end = endDate || data?.period?.end;
        
        const response = await fetch(`${API_BASE}/analytics/maintenance/export?start_date=${start}&end_date=${end}&format=csv`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
        });
        
        if (!response.ok) throw new Error('Export failed');
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `maintenance_analytics_${start}_${end}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error exporting:', error);
        alert('Failed to export data');
    } finally {
        setExporting(false);
    }
    };

  const PriorityBadge = ({ priority }) => {
    const config = {
      urgent: { label: 'Urgent', color: 'bg-red-500/20 text-red-400' },
      high: { label: 'High', color: 'bg-orange-500/20 text-orange-400' },
      normal: { label: 'Normal', color: 'bg-blue-500/20 text-blue-400' },
      low: { label: 'Low', color: 'bg-gray-500/20 text-gray-400' }
    };
    const c = config[priority] || config.normal;
    return <span className={`px-2 py-1 rounded-full text-xs ${c.color}`}>{c.label}</span>;
  };

  const StatusBadge = ({ status }) => {
    const config = {
      pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
      approved: { label: 'Approved', color: 'bg-green-500/20 text-green-400' },
      completed: { label: 'Completed', color: 'bg-blue-500/20 text-blue-400' },
      rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400' }
    };
    const c = config[status] || config.pending;
    return <span className={`px-2 py-1 rounded-full text-xs ${c.color}`}>{c.label}</span>;
  };

  const KpiCard = ({ title, value, subtext, icon: Icon, color }) => (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-gray-400 text-sm">{title}</p>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white">Maintenance Analytics</h2>
          <p className="text-gray-400 text-sm">Track and analyze maintenance requests performance</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-gray-700 rounded-lg p-0.5">
            {['week', 'month', 'year', 'all'].map((p) => (
              <button
                key={p}
                onClick={() => { setPeriod(p); setShowDatePicker(false); }}
                className={`px-3 py-1 text-sm rounded-md transition ${
                  period === p ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`px-3 py-1 text-sm rounded-md transition ${
                showDatePicker ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Custom
            </button>
          </div>
          <button onClick={loadData} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400">
            <RefreshCw className="w-5 h-5" />
          </button>
          <button onClick={handleExport} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400">
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Custom Date Picker */}
      {showDatePicker && (
        <div className="flex gap-3 items-end p-4 bg-gray-800/50 rounded-xl border border-gray-700">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
          </div>
          <button
            onClick={() => { loadData(); setShowDatePicker(false); }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
          >
            Apply
          </button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          title="Total Requests"
          value={data?.kpi?.total || 0}
          subtext={`${data?.kpi?.approval_rate || 0}% approval rate`}
          icon={Wrench}
          color="bg-blue-500/20 text-blue-400"
        />
        <KpiCard
          title="Urgent/High"
          value={`${data?.kpi?.urgent || 0} / ${data?.kpi?.high_priority || 0}`}
          subtext="Urgent / High priority"
          icon={AlertTriangle}
          color="bg-red-500/20 text-red-400"
        />
        <KpiCard
          title="Completed"
          value={data?.kpi?.completed || 0}
          subtext={`${data?.kpi?.completion_rate || 0}% completion rate`}
          icon={CheckCircle}
          color="bg-green-500/20 text-green-400"
        />
        <KpiCard
          title="Avg Processing"
          value={`${data?.processing_time?.avg_total_hours || 0} hrs`}
          subtext={`Approval: ${data?.processing_time?.avg_approval_hours || 0}h`}
          icon={Clock}
          color="bg-purple-500/20 text-purple-400"
        />
      </div>

      {/* Monthly Chart */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
        <h3 className="text-white font-semibold mb-4">Monthly Evolution</h3>
        <div className="space-y-2">
          {data?.monthly?.slice(0, 6).map((month, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <span className="text-xs text-gray-500 w-24">
                {new Date(month.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </span>
              <div className="flex-1">
                <div className="flex gap-1">
                  <div 
                    className="h-6 bg-blue-500 rounded"
                    style={{ width: `${(month.total / (data?.monthly?.[0]?.total || 1)) * 100}%`, maxWidth: '100%' }}
                  />
                </div>
              </div>
              <span className="text-white text-sm w-16 text-right">{month.total} req</span>
            </div>
          ))}
        </div>
      </div>

      {/* Status & Priority Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold mb-4">Status Distribution</h3>
          <div className="space-y-2">
            {data?.status_distribution?.map((status, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <StatusBadge status={status.status} />
                <div className="flex items-center gap-4">
                  <div className="w-32 bg-gray-700 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-blue-500"
                      style={{ width: `${status.percentage}%` }}
                    />
                  </div>
                  <span className="text-white text-sm w-16">{status.count} ({status.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold mb-4">Priority Distribution</h3>
          <div className="space-y-2">
            {data?.priority_distribution?.map((priority, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <PriorityBadge priority={priority.priority} />
                <div className="flex items-center gap-4">
                  <div className="w-32 bg-gray-700 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-red-500"
                      style={{ width: `${priority.percentage}%` }}
                    />
                  </div>
                  <span className="text-white text-sm w-16">{priority.count} ({priority.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Clients & Top Aircraft */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold mb-4">Top Clients</h3>
          <div className="space-y-3">
            {data?.top_clients?.slice(0, 5).map((client, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">
                    {client.company_name || `${client.first_name} ${client.last_name}`}
                  </p>
                  <p className="text-xs text-gray-500">{client.request_count} requests</p>
                </div>
                <p className="text-white text-sm">{client.completed_count} completed</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold mb-4">Top Aircraft</h3>
          <div className="space-y-3">
            {data?.top_aircraft?.slice(0, 5).map((aircraft, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">{aircraft.tail_number}</p>
                  <p className="text-xs text-gray-500">{aircraft.manufacturer} {aircraft.model}</p>
                </div>
                <p className="text-white text-sm">{aircraft.request_count} req</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Requests Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-700">
            <h3 className="text-white font-semibold">Recent Requests</h3>
        </div>
        <div className="overflow-x-auto">
            <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
                <tr>
                <th className="p-3 text-left text-gray-400 text-sm">Client</th>
                <th className="p-3 text-left text-gray-400 text-sm">Aircraft</th>
                <th className="p-3 text-left text-gray-400 text-sm">Priority</th>
                <th className="p-3 text-left text-gray-400 text-sm">Status</th>
                <th className="p-3 text-left text-gray-400 text-sm">Created</th>
                </tr>
            </thead>
            <tbody>
                {data?.recent_requests?.map((req) => (
                <tr key={req.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                    <td className="p-3 text-white text-sm">
                    {req.company_name || `${req.first_name} ${req.last_name}`}
                    </td>
                    <td className="p-3 text-gray-400 text-sm">{req.tail_number || '-'}</td>
                    <td className="p-3"><PriorityBadge priority={req.priority} /></td>
                    <td className="p-3"><StatusBadge status={req.status} /></td>
                    <td className="p-3 text-gray-400 text-sm">
                    {new Date(req.created_at).toLocaleDateString()}
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </div>
        </div>
    </div>
  );
};

export default MaintenanceAnalytics;